/**
 * ProductsPage — the product catalog (BACKLOG-1752, Bill 2026-10-06: "Admin panel is unable to edit
 * products. This needs to be easier to manage in bulk and a more modern UI, intuitive and efficient").
 *
 *   - One grid: search, category and status filters, sortable columns.
 *   - Inline: the Active switch and the sort order save on the spot.
 *   - "Shown to bankers": active AND inside the phase rollout scope (rollout.* settings, seed 288) —
 *     the same rule ROP-teams applies (apps/ROP-teams/src/modules/RAO/rolloutProducts.ts).
 *   - Select rows → bulk activate / deactivate / set minimum deposit / renumber sort order
 *     (POST /v3/admin/products/bulk, partial per product).
 *   - Edit opens a side drawer with every field, grouped; jhaProdCode is carried (the old editor
 *     omitted it, so every save of an existing product was refused).
 *
 * Writes: client.retail.admin.products.products.{upsert,bulk,remove}('', …) — '' = no bypass key
 * (bearer + Admin role).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import type { Product } from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Pill, Spinner } from '@/components/primitives';
import { TextField, TextArea, SelectField, BoolField, NumberField } from '@/components/FormField';
import type { ApiClient } from '@retail-ops/api-client';

type ProductUpsertBody = Parameters<ApiClient['retail']['admin']['products']['products']['upsert']>[1];

interface CardTypeRow {
  code: string;
  description?: string | null;
}

/** Product as the config feed returns it (the shared type omits a few columns the feed carries). */
type Row = Product & {
  jhaProdCode?: string | null;
  callClassCode?: string | null;
  customerTypes?: ReadonlyArray<string> | null;
  businessOnly?: boolean | null;
  personalOnly?: boolean | null;
  minAgePrimary?: number | null;
  maxAgePrimary?: number | null;
  minAgeCoOwner?: number | null;
  maxAgeCoOwner?: number | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
};

const CATEGORIES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'DDA', label: 'Checking' },
  { value: 'SAV', label: 'Savings' },
  { value: 'CD', label: 'Certificates (CD)' },
  { value: 'IRA', label: 'IRAs' },
  { value: 'BOX', label: 'Safe deposit box' },
  { value: 'Service', label: 'Service' },
];
const CATEGORY_LABEL = new Map(CATEGORIES.map((c) => [c.value, c.label]));

/** Rollout scope: product category key per Core account type (ROP-teams ProductCategoryPickerModal). */
const ROLLOUT_CATEGORY_BY_ACCT: Record<string, string> = { '10': 'checking', '20': 'savings', '30': 'certificates', '60': 'iras', '50': 'sdb' };
/** Entity types per entity category (ROP-teams entityGroups.ts — keep in step). */
const GROUP_TYPES: Record<string, ReadonlyArray<string>> = {
  personal: ['Individual', 'Minor', 'NonResidentAlien', 'UTMA'],
  business: ['Corporation', 'LLC', 'SoleProp', 'Partnership'],
  trust: ['RevocableTrust', 'IrrevocableTrust', 'Estate'],
  'special-purpose': ['QIT', 'SelfSettledSNT', 'IOLTA', 'MedicalSetAside', 'USDAGrant', 'ICS_Business', 'ICS_Personal'],
  fiduciary: ['RepPayee', 'Conservator', 'VAFiduciary'],
  charitable: ['NonProfit', 'UnincorpAssoc', 'Donation'],
  government: ['PublicFund', 'Political'],
};

function csvAllows(raw: string | undefined, key: string): boolean {
  if (raw === undefined) return true;
  const list = raw.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
  return list.length === 0 || list.includes('*') || list.includes(key.toLowerCase());
}

interface Scope {
  groups: string | undefined;
  categories: string | undefined;
}

function phaseReason(p: Row, scope: Scope): string | null {
  const cat = ROLLOUT_CATEGORY_BY_ACCT[p.acctType ?? ''];
  if (cat && !csvAllows(scope.categories, cat)) return 'Category not offered in this phase';
  const enabledTypes = Object.entries(GROUP_TYPES)
    .filter(([g]) => csvAllows(scope.groups, g))
    .flatMap(([, t]) => t);
  const types = p.customerTypes ?? [];
  if (types.length > 0 && !types.some((t) => enabledTypes.includes(t))) return 'Owner type not offered in this phase';
  if (p.businessOnly && !enabledTypes.some((t) => GROUP_TYPES.business.includes(t))) return 'Business only — not offered in this phase';
  return null;
}

interface Draft {
  isNew: boolean;
  code: string; jhaProdCode: string; name: string; description: string; category: string; acctType: string;
  glCode: string; callClassCode: string; rateCode: string; stmtMsgCode: string; cardTypeCode: string;
  minAge: number | null; maxAge: number | null; minimumDeposit: number | null;
  maxJointOwners: number | null; maxBeneficiaries: number | null; sortOrder: number | null;
  requiresGuardian: boolean; allowJoints: boolean; allowBeneficiaries: boolean; allowDebitCard: boolean;
  allowCardLink: boolean; allowNetTeller: boolean; allowOdp: boolean; allowRegE: boolean;
  businessOnly: boolean; personalOnly: boolean; isActive: boolean;
}

const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

function toDraft(p: Row): Draft {
  return {
    isNew: false, code: p.code, jhaProdCode: p.jhaProdCode ?? p.code, name: p.name, description: p.description ?? '',
    category: p.category ?? 'DDA', acctType: p.acctType ?? '', glCode: p.glCode ?? '', callClassCode: p.callClassCode ?? '',
    rateCode: p.rateCode ?? '', stmtMsgCode: p.stmtMsgCode ?? '', cardTypeCode: p.cardTypeCode ?? '',
    minAge: num(p.minAge), maxAge: num(p.maxAge), minimumDeposit: num(p.minimumDeposit),
    maxJointOwners: num(p.maxJointOwners), maxBeneficiaries: num(p.maxBeneficiaries), sortOrder: num(p.sortOrder),
    requiresGuardian: !!p.requiresGuardian, allowJoints: !!p.allowJoints, allowBeneficiaries: !!p.allowBeneficiaries,
    allowDebitCard: !!p.allowDebitCard, allowCardLink: !!p.allowCardLink, allowNetTeller: !!p.allowNetTeller,
    allowOdp: !!p.allowOdp, allowRegE: !!p.allowRegE, businessOnly: !!p.businessOnly, personalOnly: !!p.personalOnly,
    isActive: p.isActive,
  };
}

function blank(): Draft {
  return {
    isNew: true, code: '', jhaProdCode: '', name: '', description: '', category: 'DDA', acctType: '10', glCode: '',
    callClassCode: '', rateCode: '', stmtMsgCode: '', cardTypeCode: '', minAge: null, maxAge: null, minimumDeposit: null,
    maxJointOwners: null, maxBeneficiaries: null, sortOrder: null, requiresGuardian: false, allowJoints: true,
    allowBeneficiaries: false, allowDebitCard: false, allowCardLink: false, allowNetTeller: false, allowOdp: false,
    allowRegE: false, businessOnly: false, personalOnly: false, isActive: true,
  };
}

function toBody(d: Draft): ProductUpsertBody {
  const t = (s: string): string => s.trim();
  const n = (s: string): string | null => (s.trim() === '' ? null : s.trim());
  return {
    code: t(d.code), jhaProdCode: t(d.jhaProdCode), name: t(d.name), description: n(d.description), category: t(d.category),
    acctType: t(d.acctType), glCode: t(d.glCode), callClassCode: t(d.callClassCode), rateCode: n(d.rateCode),
    stmtMsgCode: n(d.stmtMsgCode), cardTypeCode: n(d.cardTypeCode), minAge: d.minAge, maxAge: d.maxAge,
    minimumDeposit: d.minimumDeposit, requiresGuardian: d.requiresGuardian, allowJoints: d.allowJoints,
    maxJointOwners: d.maxJointOwners ?? undefined, allowBeneficiaries: d.allowBeneficiaries,
    maxBeneficiaries: d.maxBeneficiaries ?? undefined, allowDebitCard: d.allowDebitCard, allowCardLink: d.allowCardLink,
    allowNetTeller: d.allowNetTeller, allowOdp: d.allowOdp, allowRegE: d.allowRegE, businessOnly: d.businessOnly,
    personalOnly: d.personalOnly, sortOrder: d.sortOrder, isActive: d.isActive,
  } as ProductUpsertBody;
}

function missingRequired(d: Draft): string[] {
  const out: string[] = [];
  if (!d.code.trim()) out.push('Code');
  if (!d.jhaProdCode.trim()) out.push('JHA product code');
  if (!d.name.trim()) out.push('Name');
  if (!d.acctType.trim()) out.push('Account type');
  if (!d.glCode.trim()) out.push('GL code');
  return out;
}

const S: Record<string, CSSProperties> = {
  bar: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 },
  search: { flex: '1 1 240px', padding: '8px 10px', border: '1px solid var(--ink-300, #cbd5e1)', borderRadius: 6, fontSize: 14 },
  chip: { padding: '5px 10px', borderRadius: 999, border: '1px solid var(--ink-300, #cbd5e1)', background: '#fff', fontSize: 12, cursor: 'pointer' },
  chipOn: { background: 'var(--pcb-blue, #2968ad)', color: '#fff', borderColor: 'var(--pcb-blue, #2968ad)' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid var(--ink-200, #e2e8f0)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--ink-600, #475569)', cursor: 'pointer', whiteSpace: 'nowrap' },
  td: { padding: '8px 10px', borderBottom: '1px solid var(--ink-100, #f1f5f9)', verticalAlign: 'middle' },
  bulk: { position: 'sticky', top: 0, zIndex: 2, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '10px 12px', marginBottom: 10, background: 'var(--pcb-blue-dark, #1a406a)', color: '#fff', borderRadius: 8 },
  drawerBack: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.35)', zIndex: 50 },
  drawer: { position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(560px, 100vw)', background: '#fff', zIndex: 51, boxShadow: '-8px 0 30px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column' },
  section: { border: '1px solid var(--ink-200, #e2e8f0)', borderRadius: 8, padding: 12, marginBottom: 12 },
  sectionH: { margin: '0 0 8px', fontSize: 13, color: 'var(--pcb-blue-dark, #1a406a)' },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 },
};

function Switch({ on, disabled, onChange, label }: { on: boolean; disabled?: boolean; onChange: (v: boolean) => void; label: string }): JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!on)}
      style={{
        width: 38, height: 22, borderRadius: 11, border: 'none', padding: 2, cursor: disabled ? 'wait' : 'pointer',
        background: on ? 'var(--pcb-green, #386732)' : 'var(--ink-300, #cbd5e1)', transition: 'background .15s',
      }}
    >
      <span style={{ display: 'block', width: 18, height: 18, borderRadius: 9, background: '#fff', transform: on ? 'translateX(16px)' : 'none', transition: 'transform .15s' }} />
    </button>
  );
}

type SortKey = 'sortOrder' | 'code' | 'name' | 'category' | 'minimumDeposit' | 'shown';

export function ProductsPage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<Row> | null>(null);
  const [cardTypes, setCardTypes] = useState<ReadonlyArray<CardTypeRow>>([]);
  const [scope, setScope] = useState<Scope>({ groups: undefined, categories: undefined });
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState<string>('all');
  const [status, setStatus] = useState<'active' | 'inactive' | 'shown' | 'all'>('active');
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'sortOrder', dir: 1 });
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [busyCodes, setBusyCodes] = useState<ReadonlySet<string>>(new Set());
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [bulkDeposit, setBulkDeposit] = useState('');

  const reload = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      const [prodResp, cardResp, setResp] = await Promise.all([
        client.retail.config.products(),
        client.retail.config.cardTypes(),
        client.retail.config.systemSettings(),
      ]);
      if (!prodResp.success || !Array.isArray(prodResp.data)) {
        setError(prodResp.error ?? 'Failed to load products.');
        return;
      }
      setRows(prodResp.data as ReadonlyArray<Row>);
      setCardTypes(cardResp.success && Array.isArray(cardResp.data) ? (cardResp.data as unknown as CardTypeRow[]) : []);
      const raw = setResp.success ? (setResp.data as unknown) : null;
      const read = (k: string): string | undefined => {
        if (Array.isArray(raw)) {
          const r = (raw as Array<{ settingKey?: string; settingValue?: string }>).find((x) => x.settingKey === k);
          return r?.settingValue ?? undefined;
        }
        const v = raw && typeof raw === 'object' ? (raw as Record<string, unknown>)[k] : undefined;
        return typeof v === 'string' ? v : undefined;
      };
      setScope({ groups: read('rollout.enabledEntityGroups'), categories: read('rollout.enabledProductCategories') });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [client]);

  useEffect(() => { void reload(); }, [reload]);

  const view = useMemo(() => {
    if (!rows) return null;
    const needle = q.trim().toLowerCase();
    const list = rows.filter((p) => {
      if (cat !== 'all' && (p.category ?? '') !== cat) return false;
      const shown = p.isActive && phaseReason(p, scope) === null;
      if (status === 'active' && !p.isActive) return false;
      if (status === 'inactive' && p.isActive) return false;
      if (status === 'shown' && !shown) return false;
      if (!needle) return true;
      return [p.code, p.name, p.jhaProdCode ?? '', p.description ?? ''].some((s) => s.toLowerCase().includes(needle));
    });
    const val = (p: Row): string | number => {
      switch (sort.key) {
        case 'code': return p.code;
        case 'name': return p.name.toLowerCase();
        case 'category': return p.category ?? '';
        case 'minimumDeposit': return num(p.minimumDeposit) ?? -1;
        case 'shown': return p.isActive && phaseReason(p, scope) === null ? 0 : 1;
        default: return num(p.sortOrder) ?? 9999;
      }
    };
    return [...list].sort((a, b) => {
      const va = val(a); const vb = val(b);
      return (va < vb ? -1 : va > vb ? 1 : a.code.localeCompare(b.code)) * sort.dir;
    });
  }, [rows, q, cat, status, sort, scope]);

  const counts = useMemo(() => {
    const all = rows ?? [];
    return {
      active: all.filter((p) => p.isActive).length,
      inactive: all.filter((p) => !p.isActive).length,
      shown: all.filter((p) => p.isActive && phaseReason(p, scope) === null).length,
      all: all.length,
    };
  }, [rows, scope]);

  const bulk = useCallback(async (changes: Array<{ code: string; isActive?: boolean; sortOrder?: number; minimumDeposit?: number }>, label: string): Promise<boolean> => {
    setError(null); setNotice(null);
    setBusyCodes(new Set(changes.map((c) => c.code)));
    try {
      const resp = await client.retail.admin.products.products.bulk('', { changes });
      if (resp.success === false) { setError(resp.error ?? 'The change was not saved.'); return false; }
      setNotice(label);
      await reload();
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return false;
    } finally {
      setBusyCodes(new Set());
    }
  }, [client, reload]);

  const toggleSel = (code: string): void => setSelected((s) => {
    const n = new Set(s);
    if (n.has(code)) n.delete(code); else n.add(code);
    return n;
  });
  const allVisibleSelected = !!view && view.length > 0 && view.every((p) => selected.has(p.code));
  const selectAllVisible = (): void => setSelected(allVisibleSelected ? new Set() : new Set((view ?? []).map((p) => p.code)));
  const selCodes = [...selected];

  const save = useCallback(async (d: Draft): Promise<void> => {
    const missing = missingRequired(d);
    if (missing.length > 0) { setSaveErr(`Required: ${missing.join(', ')}.`); return; }
    setSaving(true); setSaveErr(null);
    try {
      const resp = await client.retail.admin.products.products.upsert('', toBody(d));
      if (resp.success === false) { setSaveErr(resp.error ?? 'Save failed.'); return; }
      setDraft(null);
      setNotice(`${d.code.trim()} saved.`);
      await reload();
    } catch (e) {
      setSaveErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [client, reload]);

  const header = (key: SortKey, label: string, style?: CSSProperties): JSX.Element => (
    <th
      style={{ ...S.th, ...style }}
      onClick={() => setSort((s) => ({ key, dir: s.key === key ? (s.dir === 1 ? -1 : 1) : 1 }))}
      aria-sort={sort.key === key ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}
    >
      {label}{sort.key === key ? (sort.dir === 1 ? ' ▲' : ' ▼') : ''}
    </th>
  );

  const cardTypeOptions = [{ value: '', label: '— none —' }, ...cardTypes.map((c) => ({ value: c.code, label: `${c.code} — ${c.description ?? ''}` }))];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ margin: 0 }}>Products</h2>
          <p style={{ margin: '4px 0 12px', color: 'var(--ink-600, #475569)', fontSize: 13 }}>
            {counts.shown} shown to bankers · {counts.active} active · {counts.inactive} inactive. “Shown to bankers” = active and inside
            this phase’s rollout scope (System → Settings, Rollout).
          </p>
        </div>
        <Button onClick={() => { setSaveErr(null); setDraft(blank()); }}>New product</Button>
      </div>

      {error && <Banner tone="err">{error}</Banner>}
      {notice && <Banner tone="info">{notice}</Banner>}

      <div style={S.bar}>
        <input style={S.search} placeholder="Search code, name, JHA code…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search products" />
        {(['shown', 'active', 'inactive', 'all'] as const).map((s) => (
          <button key={s} type="button" style={{ ...S.chip, ...(status === s ? S.chipOn : {}) }} onClick={() => setStatus(s)}>
            {s === 'shown' ? `Shown (${counts.shown})` : s === 'active' ? `Active (${counts.active})` : s === 'inactive' ? `Inactive (${counts.inactive})` : `All (${counts.all})`}
          </button>
        ))}
      </div>
      <div style={S.bar}>
        <button type="button" style={{ ...S.chip, ...(cat === 'all' ? S.chipOn : {}) }} onClick={() => setCat('all')}>All categories</button>
        {CATEGORIES.map((c) => (
          <button key={c.value} type="button" style={{ ...S.chip, ...(cat === c.value ? S.chipOn : {}) }} onClick={() => setCat(c.value)}>{c.label}</button>
        ))}
      </div>

      {selCodes.length > 0 && (
        <div style={S.bulk} role="toolbar" aria-label="Bulk actions">
          <strong>{selCodes.length} selected</strong>
          <Button size="sm" onClick={() => void bulk(selCodes.map((code) => ({ code, isActive: true })), `${selCodes.length} activated.`)}>Activate</Button>
          <Button size="sm" variant="ghost" onClick={() => void bulk(selCodes.map((code) => ({ code, isActive: false })), `${selCodes.length} deactivated.`)}>Deactivate</Button>
          <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
            <input
              value={bulkDeposit}
              onChange={(e) => setBulkDeposit(e.target.value.replace(/[^0-9.]/g, ''))}
              placeholder="Min deposit $"
              style={{ width: 110, padding: '4px 6px', borderRadius: 4, border: 'none' }}
              aria-label="Minimum deposit for selected"
            />
            <Button size="sm" variant="ghost" disabled={bulkDeposit === ''} onClick={() => void bulk(selCodes.map((code) => ({ code, minimumDeposit: Number(bulkDeposit) })), `Minimum deposit set on ${selCodes.length}.`).then((ok) => { if (ok) setBulkDeposit(''); })}>Set</Button>
          </span>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              // Renumber the selection in the order shown, 10, 20, 30… — the order bankers see.
              const ordered = (view ?? []).filter((p) => selected.has(p.code));
              void bulk(ordered.map((p, i) => ({ code: p.code, sortOrder: (i + 1) * 10 })), `Sort order renumbered for ${ordered.length}.`);
            }}
          >
            Renumber in this order
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Clear</Button>
        </div>
      )}

      {!view ? <Spinner /> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={{ ...S.th, width: 32, cursor: 'default' }}>
                  <input type="checkbox" checked={allVisibleSelected} onChange={selectAllVisible} aria-label="Select all shown" />
                </th>
                {header('sortOrder', 'Order', { width: 70 })}
                {header('code', 'Code')}
                {header('name', 'Name')}
                {header('category', 'Category')}
                {header('minimumDeposit', 'Min deposit', { textAlign: 'right' })}
                <th style={{ ...S.th, cursor: 'default' }}>Active</th>
                {header('shown', 'Shown to bankers')}
                <th style={{ ...S.th, cursor: 'default' }} />
              </tr>
            </thead>
            <tbody>
              {view.length === 0 && (
                <tr><td style={S.td} colSpan={9}>No products match.</td></tr>
              )}
              {view.map((p) => {
                const reason = phaseReason(p, scope);
                const busy = busyCodes.has(p.code);
                return (
                  <tr key={p.code} style={{ background: selected.has(p.code) ? 'var(--info-bg, #eef4fb)' : undefined, opacity: p.isActive ? 1 : 0.6 }}>
                    <td style={S.td}><input type="checkbox" checked={selected.has(p.code)} onChange={() => toggleSel(p.code)} aria-label={`Select ${p.code}`} /></td>
                    <td style={S.td}>
                      <input
                        type="number"
                        defaultValue={num(p.sortOrder) ?? ''}
                        key={`${p.code}-${p.sortOrder ?? ''}`}
                        style={{ width: 56, padding: '3px 5px', border: '1px solid var(--ink-200, #e2e8f0)', borderRadius: 4 }}
                        aria-label={`Sort order for ${p.code}`}
                        onBlur={(e) => {
                          const v = num(e.currentTarget.value);
                          if (v !== null && v !== num(p.sortOrder)) void bulk([{ code: p.code, sortOrder: v }], `${p.code} moved to ${v}.`);
                        }}
                      />
                    </td>
                    <td style={{ ...S.td, fontFamily: 'ui-monospace, Consolas, monospace' }}>{p.code}</td>
                    <td style={S.td}>
                      <div style={{ fontWeight: 600 }}>{p.name}</div>
                      {p.jhaProdCode && p.jhaProdCode !== p.code && <div style={{ fontSize: 11, color: 'var(--ink-500, #64748b)' }}>JHA {p.jhaProdCode}</div>}
                    </td>
                    <td style={S.td}>{CATEGORY_LABEL.get(p.category ?? '') ?? p.category}</td>
                    <td style={{ ...S.td, textAlign: 'right' }}>{num(p.minimumDeposit) !== null ? `$${num(p.minimumDeposit)!.toLocaleString()}` : '—'}</td>
                    <td style={S.td}>
                      <Switch on={p.isActive} disabled={busy} label={`${p.code} active`} onChange={(v) => void bulk([{ code: p.code, isActive: v }], `${p.code} ${v ? 'activated' : 'deactivated'}.`)} />
                    </td>
                    <td style={S.td}>
                      {!p.isActive ? <Pill tone="muted">Inactive</Pill>
                        : reason ? <span title={reason}><Pill tone="warn">Hidden — {reason.toLowerCase()}</Pill></span>
                        : <Pill tone="ok">Shown</Pill>}
                    </td>
                    <td style={{ ...S.td, textAlign: 'right' }}>
                      <Button size="sm" variant="ghost" onClick={() => { setSaveErr(null); setDraft(toDraft(p)); }}>Edit</Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {draft && (
        <>
          <div style={S.drawerBack} onClick={() => !saving && setDraft(null)} aria-hidden />
          <aside style={S.drawer} role="dialog" aria-modal="true" aria-label={draft.isNew ? 'New product' : `Edit ${draft.code}`}>
            <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--ink-200, #e2e8f0)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>{draft.isNew ? 'New product' : `${draft.code} — ${draft.name}`}</h3>
              <Button size="sm" variant="ghost" onClick={() => setDraft(null)} disabled={saving}>Close</Button>
            </div>
            <div style={{ padding: 16, overflowY: 'auto', flex: 1 }}>
              <div style={S.section}>
                <h4 style={S.sectionH}>Product</h4>
                <div style={S.grid2}>
                  <TextField label="Code" required value={draft.code} onChange={(v) => setDraft({ ...draft, code: v })} disabled={!draft.isNew} />
                  <TextField label="JHA product code" required hint="Sent to Core Director on AcctAdd" value={draft.jhaProdCode} onChange={(v) => setDraft({ ...draft, jhaProdCode: v })} />
                </div>
                <TextField label="Name" required value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} />
                <TextArea label="Description (what bankers read)" value={draft.description} onChange={(v) => setDraft({ ...draft, description: v })} />
                <div style={S.grid2}>
                  <SelectField label="Category" required value={draft.category} onChange={(v) => setDraft({ ...draft, category: v })} options={CATEGORIES} />
                  <NumberField label="Sort order" value={draft.sortOrder} onChange={(v) => setDraft({ ...draft, sortOrder: v })} />
                </div>
                <BoolField label="Active" value={draft.isActive} onChange={(v) => setDraft({ ...draft, isActive: v })} />
              </div>
              <div style={S.section}>
                <h4 style={S.sectionH}>Core Director codes</h4>
                <div style={S.grid2}>
                  <TextField label="Account type" required value={draft.acctType} onChange={(v) => setDraft({ ...draft, acctType: v })} />
                  <TextField label="GL code" required value={draft.glCode} onChange={(v) => setDraft({ ...draft, glCode: v })} />
                  <TextField label="Call class code" value={draft.callClassCode} onChange={(v) => setDraft({ ...draft, callClassCode: v })} />
                  <TextField label="Rate code" value={draft.rateCode} onChange={(v) => setDraft({ ...draft, rateCode: v })} />
                  <TextField label="Statement message code" value={draft.stmtMsgCode} onChange={(v) => setDraft({ ...draft, stmtMsgCode: v })} />
                  <SelectField label="Card type" value={draft.cardTypeCode} onChange={(v) => setDraft({ ...draft, cardTypeCode: v })} options={cardTypeOptions} />
                </div>
              </div>
              <div style={S.section}>
                <h4 style={S.sectionH}>Eligibility &amp; opening</h4>
                <div style={S.grid2}>
                  <NumberField label="Minimum age" value={draft.minAge} onChange={(v) => setDraft({ ...draft, minAge: v })} />
                  <NumberField label="Maximum age" value={draft.maxAge} onChange={(v) => setDraft({ ...draft, maxAge: v })} />
                  <NumberField label="Minimum deposit ($)" value={draft.minimumDeposit} onChange={(v) => setDraft({ ...draft, minimumDeposit: v })} />
                </div>
                <BoolField label="Requires a guardian / custodian" value={draft.requiresGuardian} onChange={(v) => setDraft({ ...draft, requiresGuardian: v })} />
                <div style={S.grid2}>
                  <BoolField label="Business only" value={draft.businessOnly} onChange={(v) => setDraft({ ...draft, businessOnly: v })} />
                  <BoolField label="Personal only" value={draft.personalOnly} onChange={(v) => setDraft({ ...draft, personalOnly: v })} />
                </div>
                <p style={{ fontSize: 12, color: 'var(--ink-600, #475569)', margin: '6px 0 0' }}>
                  Which owner types may hold it: Products → Customer types. Features: Products → Product features.
                </p>
              </div>
              <div style={S.section}>
                <h4 style={S.sectionH}>Owners &amp; services</h4>
                <div style={S.grid2}>
                  <BoolField label="Joint owners allowed" value={draft.allowJoints} onChange={(v) => setDraft({ ...draft, allowJoints: v })} />
                  <NumberField label="Max joint owners" value={draft.maxJointOwners} onChange={(v) => setDraft({ ...draft, maxJointOwners: v })} />
                  <BoolField label="Beneficiaries (POD) allowed" value={draft.allowBeneficiaries} onChange={(v) => setDraft({ ...draft, allowBeneficiaries: v })} />
                  <NumberField label="Max beneficiaries" value={draft.maxBeneficiaries} onChange={(v) => setDraft({ ...draft, maxBeneficiaries: v })} />
                  <BoolField label="Debit card" value={draft.allowDebitCard} onChange={(v) => setDraft({ ...draft, allowDebitCard: v })} />
                  <BoolField label="Card link" value={draft.allowCardLink} onChange={(v) => setDraft({ ...draft, allowCardLink: v })} />
                  <BoolField label="Online banking" value={draft.allowNetTeller} onChange={(v) => setDraft({ ...draft, allowNetTeller: v })} />
                  <BoolField label="Overdraft privilege" value={draft.allowOdp} onChange={(v) => setDraft({ ...draft, allowOdp: v })} />
                  <BoolField label="Reg E" value={draft.allowRegE} onChange={(v) => setDraft({ ...draft, allowRegE: v })} />
                </div>
              </div>
            </div>
            <div style={{ padding: '12px 16px', borderTop: '1px solid var(--ink-200, #e2e8f0)', display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
              {saveErr && <span style={{ color: 'var(--pcb-red, #b10505)', fontSize: 13, marginRight: 'auto' }}>{saveErr}</span>}
              <Button variant="ghost" onClick={() => setDraft(null)} disabled={saving}>Cancel</Button>
              <Button onClick={() => void save(draft)} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
            </div>
          </aside>
        </>
      )}
    </div>
  );
}
